import { Flex, Group, Skeleton } from '@mantine/core';
import { ReactNode } from 'react';
import { skyDark } from '../../styles/design-tokens';
import { H3, Text } from '../../ui-library/typography/typography';
import { OverviewCard } from './overview-card';

type Props = {
  title: string | ReactNode;
  icon?: string;
  status?: string | ReactNode;
  value: string | number;
  unit?: string;
  /**
   * When true, renders a Skeleton in place of the value/unit so the tile keeps
   * its title, icon, and outer dimensions while data is on its initial fetch.
   * Pass alongside `value` so the skeleton swaps cleanly to the real number
   * without layout shift.
   */
  isLoading?: boolean;
};

/**
 * Card that displays a single titled metric value with optional icon, status
 * row, and unit suffix. When `isLoading` is true, the value/unit area renders
 * as a Mantine Skeleton sized to the rendered `H3` so the tile preserves
 * footprint while the number resolves.
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} The rendered overview card
 */
export const SingleValueCard = ({ title, icon, status, value, unit, isLoading = false }: Props) => {
  const content = (
    <Flex direction="column" gap={8} h="100%">
      {status && <div style={{ flexGrow: 1 }}>{status}</div>}

      <Flex align="center" justify="center" h="100%" mih={48} style={{ marginTop: status ? 'auto' : 0 }}>
        {isLoading ? (
          <Skeleton height={32} width="60%" radius="sm" data-testid="single-value-card-skeleton" />
        ) : (
          <Group gap={4} align="flex-end">
            <H3>{value}</H3>
            {unit && (
              <Text size="small" color={skyDark}>
                {unit}
              </Text>
            )}
          </Group>
        )}
      </Flex>
    </Flex>
  );

  return <OverviewCard title={title} icon={icon} content={content} />;
};
